import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import api, { formatApiError } from "../lib/api";
import { money, compactMoney, pct } from "../lib/format";
import { RANGE_PRESETS, quarterOptions, trailingQuarters, monthLabel } from "../lib/quarters";
import { ChartCard, SERIES_COLORS, VENDOR_COLORS, ESTIMATE_COLORS, CHART_GRID } from "./ChartCard";
import { DrillDialog } from "./DrillDialog";
import { useAuth } from "../lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const axisTick = { fontSize: 11, fill: "#6B7A82" };
const QUARTERS = quarterOptions();
const PRODUCT_LABELS = {
  ALL: "All Products", AC: "Accident (AC)", CI: "Critical Illness (CI)",
  HI: "Hospital Indemnity (HI)", PP: "Purchasing Power (PP)",
};

const MoneyTooltip = ({ active, payload, label, percent, hideTotal }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum, entry) => sum + Number(entry.value || 0), 0);
  return (
    <div className="border px-3 py-2 text-xs" style={{ background: "#FFFFFF", borderColor: "#D8D6D1" }}>
      <div className="font-heading font-bold mb-1" style={{ color: "var(--ink)" }}>{periodTick(label)}</div>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5" style={{ color: "var(--muted)" }}>
            <span className="inline-block w-2 h-2" style={{ background: entry.color }} />
            {entry.name}
          </span>
          <span style={{ color: "var(--ink)" }}>{percent ? pct(entry.value) : money(entry.value)}</span>
        </div>
      ))}
      {!percent && !hideTotal && payload.length > 1 && (
        <div className="flex items-center justify-between gap-6 mt-1 pt-1 border-t" style={{ borderColor: "#EFEEE9" }}>
          <span className="eyebrow text-[9px]">Period total</span>
          <span className="font-heading font-bold" style={{ color: "var(--ink)" }}>{money(total)}</span>
        </div>
      )}
    </div>
  );
};

const ExpenseTooltip = ({ active, payload, label, percent }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const pairs = [
    ["Carrier Fees", row.carrier_fees, row.carrier_fees_pct],
    ["Technology Fees", row.technology_fees, row.technology_fees_pct],
    ["Management Fees", row.management_fees, row.management_fees_pct],
    ...("premium_tax" in row ? [["Premium Tax", row.premium_tax, row.premium_tax_pct]] : []),
  ];
  return (
    <div className="border px-3 py-2 text-xs" style={{ background: "#FFFFFF", borderColor: "#D8D6D1" }}>
      <div className="font-heading font-bold mb-1" style={{ color: "var(--ink)" }}>{periodTick(label)}</div>
      {pairs.map(([name, amount, share]) => (
        <div key={name} className="flex items-center justify-between gap-6">
          <span style={{ color: "var(--muted)" }}>{name}</span>
          <span style={{ color: "var(--ink)" }}>
            {money(amount || 0)}{row.premium > 0 ? ` · ${pct(share || 0)}` : ""}
          </span>
        </div>
      ))}
      <div className="flex items-center justify-between gap-6 mt-1 pt-1 border-t" style={{ borderColor: "#EFEEE9" }}>
        <span className="eyebrow text-[9px]">{percent ? "Total fee load" : "Period total"}</span>
        <span className="font-heading font-bold" style={{ color: "var(--ink)" }}>
          {money(row.total_fees || 0)}{row.premium > 0 ? ` · ${pct(row.fee_load_pct || 0)}` : ""}
        </span>
      </div>
      {!row.premium && (
        <div className="mt-1 text-[10px]" style={{ color: "var(--muted)" }}>No premium recorded</div>
      )}
    </div>
  );
};

const chartFrame = (children) => (
  <div className="h-64 w-full">
    <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
  </div>
);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const periodTick = (value) => {
  const text = String(value ?? "");
  if (text.includes("-Q")) return text;
  if (!/^\d{4}-\d{2}/.test(text)) return text;
  return `${MONTHS[Number(text.slice(5, 7)) - 1]} '${text.slice(2, 4)}`;
};

const periodLabel = (value) => {
  const text = String(value || "");
  if (!text) return "—";
  if (text.includes("-Q")) return text;
  if (/^\d{4}-\d{2}$/.test(text)) return monthLabel(text);
  return text;
};

const monthsOfQuarter = (quarter) => {
  const [year, number] = String(quarter).split("-Q");
  const first = (Number(number) - 1) * 3 + 1;
  return [0, 1, 2].map((offset) => `${year}-${String(first + offset).padStart(2, "0")}`);
};

const quarterOfMonth = (month) => {
  const [year, number] = String(month).split("-");
  return `${year}-Q${Math.floor((Number(number) - 1) / 3) + 1}`;
};

const monthOptionsFor = (quarters) => quarters.flatMap(monthsOfQuarter);

const axes = (
  <>
    <CartesianGrid stroke={CHART_GRID} vertical={false} />
    <XAxis dataKey="period" tick={axisTick} axisLine={false} tickLine={false}
           tickFormatter={periodTick} interval="preserveStartEnd" />
  </>
);

const moneyAxis = (
  <YAxis tickFormatter={compactMoney} tick={axisTick} axisLine={false} tickLine={false} width={62} />
);

const FundSplit = ({ payments, active }) => {
  const byFund = payments?.by_fund;
  if (!byFund) return null;
  const total = Number(payments.total || 0);
  const cells = [
    { key: "ALL", label: "All funds", value: total },
    { key: "ERR", label: "ERR draws", value: Number(byFund.ERR?.total || 0) },
    { key: "PPR", label: "PPR draws", value: Number(byFund.PPR?.total || 0) },
  ];
  return (
    <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 border divide-y sm:divide-y-0 sm:divide-x"
         style={{ borderColor: "var(--border)" }} data-testid="vendor-fund-split">
      {cells.map((cell) => (
        <div key={cell.key} className="px-4 py-3" data-testid={`vendor-fund-split-${cell.key}`}
             style={{ borderColor: "var(--border)",
                      background: active === cell.key ? "rgba(14,58,92,0.05)" : "transparent" }}>
          <div className="eyebrow text-[9px]">{cell.label}</div>
          <div className="font-heading font-bold text-xl mt-1" style={{ color: "var(--ink)" }}>
            {money(cell.value)}
          </div>
          <div className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>
            {total ? `${((cell.value / total) * 100).toFixed(1)}% of payments in range` : "No payments in range"}
          </div>
        </div>
      ))}
    </div>
  );
};

const FeeCompare = ({ compare }) => {
  const current = compare?.current;
  if (!current) return null;
  const cells = [current, compare.prior_period, compare.prior_year].filter(Boolean);
  const delta = (entry) => {
    if (entry === current || !entry.has_data || !current.has_data) return null;
    const diff = Number(current.fee_load_pct || 0) - Number(entry.fee_load_pct || 0);
    if (!diff) return "No change";
    return `${diff > 0 ? "+" : "−"}${Math.abs(diff).toFixed(2)} pts vs today`;
  };
  return (
    <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 border divide-y sm:divide-y-0 sm:divide-x"
         style={{ borderColor: "var(--border)" }} data-testid="fee-compare">
      {cells.map((entry, index) => (
        <div key={entry.label} className="px-4 py-3" data-testid={`fee-compare-${index}`}
             style={{ borderColor: "var(--border)" }}>
          <div className="eyebrow text-[9px]">{entry.label}</div>
          <div className="font-heading font-bold text-xl mt-1" style={{ color: "var(--ink)" }}>
            {entry.has_data ? `${Number(entry.fee_load_pct || 0).toFixed(2)}%` : "—"}
          </div>
          <div className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>
            {entry.has_data
              ? `${money(entry.total_fees)} fees on ${money(entry.premium)} premium`
              : entry.before_program
                ? "This window is before the program started"
                : "No data on file for this window"}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--muted)" }}>
            {entry.range}{delta(entry) ? ` · ${delta(entry)}` : ""}
          </div>
        </div>
      ))}
    </div>
  );
};

const SCOPES = [["inception", "Since Inception"], ["period", "Selected Period"]];

const longDate = (value) => value.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

function inceptionRangeCaption(programStart, firstMonth, throughMonth) {
  if (!firstMonth || !throughMonth) return "Since inception: all activity held for this program";
  const start = programStart
    ? new Date(`${String(programStart).slice(0, 10)}T00:00:00Z`)
    : new Date(`${firstMonth.slice(0, 7)}-01T00:00:00Z`);
  const [year, month] = throughMonth.slice(0, 7).split("-").map(Number);
  const end = new Date(Date.UTC(year, month, 0));
  return `Since inception: ${longDate(start)} – ${longDate(end)}`
    + (programStart ? "" : " (earliest data held; no program start date on file)");
}

function ScopeToggle({ value, onChange, testId }) {
  return (
    <div className="flex items-center border h-8" style={{ borderColor: "var(--border)" }} data-testid={`${testId}-toggle`}>
      {SCOPES.map(([key, label]) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className="px-2.5 h-full text-[11px] whitespace-nowrap"
          style={{
            background: value === key ? "var(--ink)" : "transparent",
            color: value === key ? "var(--surface)" : "var(--muted)",
          }}
          data-testid={`${testId}-${key}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}


export default function ProgramFinancials({ isAdmin = false }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fallback = useMemo(() => trailingQuarters(8), []);
  const from = params.get("from") || fallback.from;
  const to = params.get("to") || fallback.to;
  const product = params.get("product") || "ALL";
  const company = params.get("company") || "";
  const planType = params.get("plan") || "";
  const insurer = params.get("insurer") || "";
  const carrier = params.get("carrier") || "";
  const vendorDetail = params.get("vendors") === "all" ? "all" : "top";
  const vendorFund = ["ERR", "PPR"].includes(params.get("fund")) ? params.get("fund") : "ALL";
  const showEstimates = params.get("est") === "1";
  const granularity = params.get("gran") === "month" ? "month" : "quarter";
  const [drill, setDrill] = useState(null);
  const [expenseMode, setExpenseMode] = useState("dollars");
  const [errScope, setErrScope] = useState("inception");
  const [summaryScope, setSummaryScope] = useState("inception");
  const [expenseScope, setExpenseScope] = useState("inception");

  const setControl = useCallback((changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value); else next.delete(key);
    });
    setParams(next, { replace: true });
  }, [params, setParams]);

  const openDrill = useCallback((source, title) => (state) => {
    const period = state?.activeLabel;
    if (period) setDrill({ source, title, period });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const isMonthRange = granularity === "month" && /^\d{4}-\d{2}$/.test(from);
    api.get("/financials/dashboard", {
      params: { from_quarter: isMonthRange ? quarterOfMonth(from) : from,
                to_quarter: isMonthRange ? quarterOfMonth(to) : to,
                from_month: isMonthRange ? from : "", to_month: isMonthRange ? to : "",
                product, company, plan_type: planType,
                insurer, carrier, vendor_detail: vendorDetail, granularity },
    })
      .then(({ data: payload }) => { if (!cancelled) setData(payload); })
      .catch((error) => toast.error(formatApiError(error.response?.data?.detail)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, product, company, planType, insurer, carrier, vendorDetail, granularity]);

  const d = data || {};
  const hasFin = Boolean(d.has_financials);
  const target = Number(d.target_loss_ratio || 70);
  const vendorData = (vendorFund === "ALL"
    ? d.vendor_payments
    : d.vendor_payments?.by_fund?.[vendorFund]) || {};
  const vendors = vendorData.vendors || [];
  const term = d.surplus_word === "Surplus" ? "Surplus" : "ERRs";
  const per = granularity === "month" ? "month" : "quarter";
  const isPP = (d.product || product) === "PP";
  const invoiceService = user?.role !== "customer" || user?.invoice_service !== false;

  const summaryInception = summaryScope === "inception";
  const expenseInception = expenseScope === "inception";
  const summaryRows = summaryInception ? (d.summary_inception || []) : (d.summary || []);

  const expenseRows = useMemo(() => ((expenseInception ? d.expenses_inception : d.expenses) || []).map((row) => {
    const premium = Number(row.premium || 0);
    const share = (value) => (premium > 0 ? Number(((Number(value || 0) / premium) * 100).toFixed(2)) : 0);
    const total = ["carrier_fees", "technology_fees", "management_fees", "premium_tax"]
      .reduce((sum, key) => sum + Number(row[key] || 0), 0);
    return {
      ...row,
      total_fees: Number(total.toFixed(2)),
      carrier_fees_pct: share(row.carrier_fees),
      technology_fees_pct: share(row.technology_fees),
      management_fees_pct: share(row.management_fees),
      ...("premium_tax" in row ? { premium_tax_pct: share(row.premium_tax) } : {}),
      fee_load_pct: share(total),
    };
  }), [d.expenses, d.expenses_inception, expenseInception]);

  const errStatusRows = errScope === "inception"
    ? (d.err_status_inception || [])
    : (d.err_status || []);

  const inceptionStart = d.program_start || d.data_from || "";
  const inceptionThrough = d.inception_through || d.data_through;
  const inceptionCaption = inceptionRangeCaption(d.program_start, inceptionStart, inceptionThrough);

  // Keep the From / To pickers in step with the chosen granularity.
  const periodOptions = granularity === "month" ? monthOptionsFor(QUARTERS) : QUARTERS;
  const available = useMemo(() => d.months_available || [], [d.months_available]);
  const clampMonth = useCallback((month) => {
    if (!available.length) return month;
    if (month < available[0]) return available[0];
    if (month > available[available.length - 1]) return available[available.length - 1];
    return month;
  }, [available]);

  const switchGranularity = useCallback((value) => {
    if (value === granularity) return;
    if (value === "month") {
      const fromMonth = clampMonth(monthsOfQuarter(from)[0]);
      const toMonth = clampMonth(monthsOfQuarter(to)[2]);
      setControl({ gran: "month", from: fromMonth, to: toMonth > fromMonth ? toMonth : fromMonth });
    } else {
      setControl({ gran: "", from: quarterOfMonth(from), to: quarterOfMonth(to) });
    }
  }, [granularity, from, to, clampMonth, setControl]);

  const vendorToggle = (
    <div className="flex items-center border h-8" style={{ borderColor: "var(--border)" }} data-testid="vendor-detail-toggle">
      {[["top", "Top 15"], ["all", "All"]].map(([value, label]) => (
        <button
          key={value}
          onClick={() => setControl({ vendors: value === "top" ? "" : value })}
          className="px-3 h-full text-xs"
          style={{
            background: vendorDetail === value ? "var(--ink)" : "transparent",
            color: vendorDetail === value ? "var(--surface)" : "var(--muted)",
          }}
          data-testid={`vendor-detail-${value}`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const fundToggle = (
    <div className="flex items-center border h-8" style={{ borderColor: "var(--border)" }} data-testid="vendor-fund-toggle">
      {[["ALL", "All funds"], ["ERR", "ERRs"], ["PPR", "PPRs"]].map(([value, label]) => (
        <button
          key={value}
          onClick={() => setControl({ fund: value === "ALL" ? "" : value })}
          className="px-3 h-full text-xs"
          style={{
            background: vendorFund === value ? "var(--ink)" : "transparent",
            color: vendorFund === value ? "var(--surface)" : "var(--muted)",
          }}
          data-testid={`vendor-fund-${value}`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const fundNote = vendorFund === "ALL"
    ? "All funds"
    : vendorFund === "PPR" ? "Purchase Power Refund draws only" : "Earned Refund draws only";
  const chartNote = (note) => isAdmin ? note.replace(/\.$/, "") : note;

  const vendorCard = (
    <ChartCard
      testId="chart-vendor-payments" title="Vendor Payments"
      actions={<div className="flex flex-wrap items-center gap-2">{fundToggle}{vendorToggle}</div>}
      note={chartNote(`${fundNote} · payments to ${vendors.length} payee(s) per ${per}, by paid date`
            + (vendorData.grouped_vendors ? `, smallest ${vendorData.grouped_vendors} grouped as Other.` : "."))}
      baseName={`vendor-payments-${vendorFund.toLowerCase()}`}
      hasData={Boolean(vendorData.has_data)}
      rows={vendorData.rows || []}
      columns={[{ key: "period", label: "Period" },
        ...vendors.map((vendor) => ({ key: vendor, label: vendor }))]}
    >
      {chartFrame(
        <BarChart data={vendorData.rows || []} onClick={openDrill("payments", `Vendor payments · ${fundNote}`)} style={{ cursor: "pointer" }}>
          {axes}{moneyAxis}
          <Tooltip content={<MoneyTooltip />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
          <Legend wrapperStyle={{ fontSize: 11, maxHeight: 72, overflowY: "auto" }} />
          {vendors.map((vendor, index) => (
            <Bar key={vendor} dataKey={vendor} name={vendor} stackId="v"
         fill={VENDOR_COLORS[index % VENDOR_COLORS.length]} />
          ))}
        </BarChart>
      )}
      <FundSplit payments={d.vendor_payments} active={vendorFund} />
    </ChartCard>
  );

  return (
    <div className="mt-10" data-testid="program-financials">
      <div className="border-b pb-4 mb-6" style={{ borderColor: "var(--border)" }}>
        <div className="eyebrow">Program financials</div>
        <h2 className="font-heading font-black text-3xl md:text-4xl mt-1 leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          How your program is operating
        </h2>
        <p className="mt-2 text-sm flex flex-wrap items-center gap-2" style={{ color: "var(--muted)" }} data-testid="data-through">
          <span>{d.data_through ? `Data loaded through ${monthLabel(d.data_through)} · ` : ""}Click any bar chart for details</span>
        </p>
      </div>

      <div
        className="sticky top-16 z-30 border flex flex-col gap-4 p-4 mb-8"
        style={{ borderColor: "var(--border)", background: "#F3F3F1" }}
        data-testid="financial-controls"
      >
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div>
            <div className="eyebrow text-[9px] mb-2">Quick range</div>
            <div className="flex flex-wrap items-center gap-1">
              {RANGE_PRESETS.map((preset) => {
                const resolved = preset.resolve();
                const active = resolved.from === from && resolved.to === to;
                return (
                  <button
                    key={preset.key}
                    onClick={() => setControl({ from: resolved.from, to: resolved.to })}
                    className="px-3 py-2 text-xs border"
                    style={{
                      borderColor: "var(--border)",
                      background: active ? "var(--ink)" : "transparent",
                      color: active ? "var(--surface)" : "var(--muted)",
                    }}
                    data-testid={`range-preset-${preset.key}`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <ControlSelect label="From" value={from} onChange={(value) => setControl({ from: value })}
                           options={periodOptions} labels={granularity === "month" ? Object.fromEntries(periodOptions.map((m) => [m, monthLabel(m)])) : undefined}
                           testId="control-from" />
            <ControlSelect label="To" value={to} onChange={(value) => setControl({ to: value })}
                           options={periodOptions} labels={granularity === "month" ? Object.fromEntries(periodOptions.map((m) => [m, monthLabel(m)])) : undefined}
                           testId="control-to" />
            <ControlSelect label="Product" value={d.product || product}
                           onChange={(value) => setControl({ product: value })}
                           options={d.products || ["ALL"]}
                           labels={PRODUCT_LABELS} testId="control-product" />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          {isAdmin && (
            <ControlSelect
              label="Carrier" value={carrier || "__all__"}
              onChange={(value) => setControl({ carrier: value === "__all__" ? "" : value, company: "" })}
              options={["__all__", ...(d.carriers || [])]}
              labels={{ __all__: "All Carriers" }} testId="control-carrier"
            />
          )}
          {isAdmin && (
            <ControlSelect
              label="Employer" value={company || "__all__"}
              onChange={(value) => setControl({ company: value === "__all__" ? "" : value })}
              options={["__all__", ...(d.companies || [])]}
              labels={{ __all__: carrier ? `All ${carrier} Employers` : "All Employers" }} testId="control-company"
            />
          )}
          <ControlSelect
            label="Plan type" value={d.plan_type || planType}
            onChange={(value) => setControl({ plan: value, product: "", insurer: "" })}
            options={(d.plan_types || []).length > 1 ? d.plan_types : []} testId="control-plan-type"
          />
          <ControlSelect
            label="Insurer" value={insurer || "__all__"}
            onChange={(value) => setControl({ insurer: value === "__all__" ? "" : value })}
            options={["__all__", ...(d.insurers || [])]}
            labels={{ __all__: d.insurers?.length > 1 ? "All Insurers" : "Insurer" }} testId="control-insurer"
          />
          <div className="flex flex-wrap items-end gap-2 sm:ml-auto">
            <div className="flex items-center border h-9" style={{ borderColor: "var(--border)" }} data-testid="granularity-toggle">
              {[["quarter", "Quarterly"], ["month", "Monthly"]].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => switchGranularity(value)}
                  className="px-3 h-full text-xs"
                  style={{
                    background: granularity === value ? "var(--ink)" : "transparent",
                    color: granularity === value ? "var(--surface)" : "var(--muted)",
                  }}
                  data-testid={`granularity-${value}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {loading && !data && (
        <div className="border p-16 text-center text-sm" style={{ borderColor: "var(--border)", color: "var(--muted)" }} data-testid="financials-loading">
          Loading program financials…
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            testId="chart-program-summary" title="Program Summary"
            note={chartNote(summaryInception
              ? `How premium has been consumed since the program began: fees, claims and reserves, and ${term}.`
              : isPP
              ? `How each ${per}'s payroll deductions split between fees and ${term}.`
              : `How each ${per}'s premium was consumed: fees, claims and reserves, and ${term}.`)}
            baseName="program-summary" hasData={hasFin} rows={summaryRows}
            caption={summaryInception ? inceptionCaption : ""}
            actions={<ScopeToggle value={summaryScope} onChange={setSummaryScope} testId="summary-scope" />}
            columns={[
              { key: "period", label: "Period" }, { key: "premium", label: "Premium" },
              { key: "fees", label: "Fees" }, { key: "claims_reserves", label: "Claims + Reserves" },
              { key: "surplus", label: term },
              { key: "est_fees", label: "Fees (est)" },
              { key: "est_claims_reserves", label: "Claims + reserves (est)" },
              { key: "est_surplus", label: `${term} (est)` },
            ]}
          >
            {chartFrame(
              <BarChart data={summaryRows} onClick={summaryInception ? undefined : openDrill("financials", "Program summary")} style={{ cursor: summaryInception ? "default" : "pointer" }}>
                {axes}{moneyAxis}
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="fees" name="Fees" stackId="s" fill={SERIES_COLORS.fees} />
                {!isPP && <Bar dataKey="claims_reserves" name="Claims + Reserves" stackId="s" fill={SERIES_COLORS.claims} />}
                <Bar dataKey="surplus" name={term} stackId="s" fill={SERIES_COLORS.surplus} />
                {showEstimates && <Bar dataKey="est_fees" name="Fees (est)" stackId="se" fill={ESTIMATE_COLORS.fees} />}
                {showEstimates && !isPP && <Bar dataKey="est_claims_reserves" name="Claims + Reserves (est)" stackId="se" fill={ESTIMATE_COLORS.claims} />}
                {showEstimates && <Bar dataKey="est_surplus" name={`${term} (est)`} stackId="se" fill={ESTIMATE_COLORS.surplus} />}
              </BarChart>
            )}
          </ChartCard>

          <ChartCard
            testId="chart-program-performance" title="Program Performance"
            note={chartNote(`${term} earned each ${per} for the selected line(s) of business.`)}
            baseName="program-performance"
            hasData={(d.err_performance || []).some((row) => row.err > 0)}
            rows={d.err_performance || []}
            columns={[{ key: "period", label: "Period" }, { key: "err", label: `${term} actual` },
                      { key: "est_err", label: `${term} estimate` }]}
          >
            {chartFrame(
              <BarChart data={d.err_performance || []} onClick={openDrill("financials", "Program performance")} style={{ cursor: "pointer" }}>
                {axes}{moneyAxis}
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Bar dataKey="err" name={term} fill={SERIES_COLORS.err} />
                {showEstimates && <Bar dataKey="est_err" name={`${term} (est)`} fill={ESTIMATE_COLORS.err} />}
              </BarChart>
            )}
          </ChartCard>

          <ChartCard
            testId="chart-err-status" title={`${term} by Status`}
            note={chartNote(errScope === "inception"
              ? `Accrued — refunds earned. Approved — amounts the Department of Insurance has allowed for release. Paid — amounts actually paid out to vendors. Available Cash — Approved minus Paid.`
              : `Accrued — refunds earned. Approved — amounts the Department of Insurance has allowed for release. Paid — amounts actually paid out to vendors. Available Cash — Approved minus Paid. Activity inside ${periodLabel(from)} to ${periodLabel(to)} only.`)}
            baseName="err-by-status" hasData={Boolean(d.has_err)} rows={errStatusRows}
            columns={[
              { key: "period", label: "Period" }, { key: "accrued", label: "Accrued" },
              { key: "authorized", label: "Approved" }, { key: "disbursed", label: "Paid" },
              { key: "available_cash", label: "Available Cash" },
            ]}
            actions={<ScopeToggle value={errScope} onChange={setErrScope} testId="err-scope" />}
            caption={errScope === "inception" ? inceptionCaption : ""}
          >
            {chartFrame(
              <BarChart data={errStatusRows}
                        onClick={errScope === "period" ? openDrill("performance", "ERR by status") : undefined}
                        style={{ cursor: errScope === "period" ? "pointer" : "default" }}>
                {axes}{moneyAxis}
                <Tooltip content={<MoneyTooltip hideTotal />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} payload={[
                  { value: "Accrued", type: "square", color: SERIES_COLORS.accrued },
                  { value: "Approved", type: "square", color: SERIES_COLORS.approved },
                  { value: "Paid", type: "square", color: SERIES_COLORS.paid },
                  { value: "Available Cash", type: "square", color: SERIES_COLORS.available_cash },
                ]} formatter={(value) => <span style={{ color: "var(--ink)" }}>{value}</span>} />
                <Bar dataKey="accrued" name="Accrued" fill={SERIES_COLORS.accrued} />
                <Bar dataKey="authorized" name="Approved" fill={SERIES_COLORS.approved} />
                <Bar dataKey="disbursed" name="Paid" fill={SERIES_COLORS.paid} />
                <Bar dataKey="available_cash" name="Available Cash" fill={SERIES_COLORS.available_cash} />
              </BarChart>
            )}
          </ChartCard>

          {!isPP && <ChartCard
            testId="chart-expenses" title="Program Expenses"
            note={chartNote((expenseMode === "percent"
              ? `Carrier, Technology and Management Fees as a share of premium${expenseInception ? " since the program began" : ` per ${per}`}. Hover any bar for the dollar figures.`
              : `Total fees${expenseInception ? " since the program began" : ` per ${per}`}, split into Carrier Fees, Technology Fees and Management Fees.`))}
            baseName="program-expenses" hasData={hasFin} rows={expenseRows}
            caption={expenseInception ? inceptionCaption : ""}
            columns={[
              { key: "period", label: "Period" },
              { key: "carrier_fees", label: "Carrier Fees" },
              { key: "technology_fees", label: "Technology Fees" },
              { key: "management_fees", label: "Management Fees" },
              ...(isAdmin && expenseRows.some((row) => "premium_tax" in row)
                ? [{ key: "premium_tax", label: "Premium Tax" }] : []),
              { key: "total_fees", label: "Total Fees" },
              { key: "premium", label: "Premium" },
              { key: "fee_load_pct", label: "Fee Load (% Of Premium)" },
            ]}
            action={undefined}
            actions={
              <div className="flex flex-wrap items-center gap-2">
              <ScopeToggle value={expenseScope} onChange={setExpenseScope} testId="expense-scope" />
              <div className="flex items-center border h-8" style={{ borderColor: "var(--border)" }} data-testid="expense-mode-toggle">
                {[["dollars", "Dollars"], ["percent", "% of Premium"]].map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setExpenseMode(value)}
                    className="px-2.5 h-full text-[11px]"
                    style={{
                      background: expenseMode === value ? "var(--ink)" : "transparent",
                      color: expenseMode === value ? "var(--surface)" : "var(--muted)",
                    }}
                    data-testid={`expense-mode-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              </div>
            }
          >
            <>
            {expenseMode === "percent" && !expenseRows.some((row) => row.premium > 0) ? (
              <div className="h-64 flex items-center justify-center px-6 text-center text-sm"
                   style={{ color: "var(--muted)" }} data-testid="expense-percent-unavailable">
                No premium is recorded for this period, so fees cannot be shown as a share of
                premium. Switch to Dollars to see the fee amounts.
              </div>
            ) : chartFrame(
              <BarChart data={expenseRows} onClick={expenseInception ? undefined : openDrill("financials", "Program Expenses")} style={{ cursor: expenseInception ? "default" : "pointer" }}>
                {axes}
                {expenseMode === "percent"
                  ? <YAxis tickFormatter={(value) => `${value}%`} tick={axisTick} axisLine={false} tickLine={false} width={48} />
                  : moneyAxis}
                <Tooltip content={<ExpenseTooltip percent={expenseMode === "percent"} />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey={expenseMode === "percent" ? "carrier_fees_pct" : "carrier_fees"}
                     name="Carrier Fees" stackId="e" fill={SERIES_COLORS.carrier_fees} />
                <Bar dataKey={expenseMode === "percent" ? "technology_fees_pct" : "technology_fees"}
                     name="Technology Fees" stackId="e" fill={SERIES_COLORS.tech_fees} />
                <Bar dataKey={expenseMode === "percent" ? "management_fees_pct" : "management_fees"}
                     name="Management Fees" stackId="e" fill={SERIES_COLORS.management_fees} />
                {isAdmin && expenseRows.some((row) => "premium_tax" in row) && (
                  <Bar dataKey={expenseMode === "percent" ? "premium_tax_pct" : "premium_tax"}
                       name="Premium Tax" stackId="e" fill={SERIES_COLORS.fees} />
                )}
                {showEstimates && expenseMode === "dollars" && (
                  <Bar dataKey="est_expenses" name="Expenses (est)" fill={ESTIMATE_COLORS.fees} />
                )}
              </BarChart>
            )}
            <FeeCompare compare={d.fee_compare} />
            </>
          </ChartCard>}

          <ChartCard
            testId="chart-premiums" title={isPP ? "Payments" : "Premiums"}
            note={chartNote(isPP
              ? `Payroll deductions collected per ${per} for Purchasing Power.`
              : `Actual paid premium per ${per} for the selected line(s) of business.`)}
            baseName="premiums" hasData={hasFin} rows={d.premiums || []}
            columns={[{ key: "period", label: "Period" },
                      { key: "premium", label: isPP ? "Payments actual" : "Premium actual" },
                      { key: "est_premium", label: isPP ? "Payments estimate" : "Premium estimate" }]}
          >
            {chartFrame(
              <BarChart data={d.premiums || []} onClick={openDrill("financials", "Premiums")} style={{ cursor: "pointer" }}>
                {axes}{moneyAxis}
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Bar dataKey="premium" name={isPP ? "Payments" : "Premium"} fill={SERIES_COLORS.premium} />
                {showEstimates && <Bar dataKey="est_premium" name={isPP ? "Payments (est)" : "Premium (est)"} fill={ESTIMATE_COLORS.premium} />}
              </BarChart>
            )}
          </ChartCard>

          {isPP && invoiceService && vendorCard}

          {!isPP && <ChartCard
            testId="chart-loss-ratio" title="Loss Ratio"
            note={isAdmin ? "Claims + Reserves / Premiums per Quarter" : `Claims plus reserves divided by premium per ${per}.`}
            baseName="loss-ratio" hasData={hasFin} rows={d.loss_ratio || []}
            columns={[
              { key: "period", label: "Period" }, { key: "loss_ratio", label: "Loss Ratio %" },
              { key: "claims_reserves", label: "Claims + Reserves" }, { key: "premium", label: "Premium" },
              { key: "est_loss_ratio", label: "Loss ratio % (est)" },
            ]}
          >
            {chartFrame(
              <BarChart data={d.loss_ratio || []} onClick={openDrill("financials", "Loss ratio")} style={{ cursor: "pointer" }}>
                {axes}
                <YAxis tickFormatter={(value) => `${value}%`} tick={axisTick} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={<MoneyTooltip percent />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Bar dataKey="loss_ratio" name="Loss ratio">
                  {(d.loss_ratio || []).map((row) => (
                    <Cell key={row.period} fill={row.loss_ratio > target ? SERIES_COLORS.ratio_over : SERIES_COLORS.ratio_ok} />
                  ))}
                </Bar>
                {showEstimates && <Bar dataKey="est_loss_ratio" name="Loss ratio (est)" fill={ESTIMATE_COLORS.ratio} />}
              </BarChart>
            )}
          </ChartCard>}

          {!isPP && <ChartCard
            testId="chart-accrued-surplus" title={`Accrued ${term}`}
            note={chartNote("Premiums less claims, reserve, fees, and tech credits, per quarter & cumulative across the range.")}
            baseName="accrued-surplus" hasData={hasFin} rows={d.accrued_surplus || []}
            columns={[
              { key: "period", label: "Period" }, { key: "surplus", label: term },
              { key: "cumulative_surplus", label: `Cumulative ${term.toLowerCase()}` },
              { key: "est_surplus", label: `${term} (est)` },
              { key: "est_cumulative_surplus", label: `Cumulative ${term.toLowerCase()} (est)` },
            ]}
          >
            {chartFrame(
              <BarChart data={d.accrued_surplus || []} onClick={openDrill("financials", `Accrued ${term}`)} style={{ cursor: "pointer" }}>
                {axes}{moneyAxis}
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="surplus" name={`${term} this ${per}`} fill={SERIES_COLORS.surplus} />
                <Bar dataKey="cumulative_surplus" name="Cumulative" fill={SERIES_COLORS.fees} />
                {showEstimates && <Bar dataKey="est_surplus" name={`${term} (est)`} fill={ESTIMATE_COLORS.surplus} />}
              </BarChart>
            )}
          </ChartCard>}


          {isAdmin && !isPP && !!(d.fee_breakdown || []).length && <ChartCard
            testId="chart-fee-breakdown" title="Fee Breakdown (from rates)"
            note={chartNote(`Fees recalculated per ${per} from the effective-dated rates table, next to the fees carried in the feed.`)}
            baseName="fee-breakdown" hasData={(d.fee_breakdown || []).some((r) => r.fees_calc || r.fees_feed)}
            rows={d.fee_breakdown || []}
            columns={[
              { key: "period", label: "Period" }, { key: "e1_fees_calc", label: "E1 fees (calc)" },
              { key: "carrier_fees_calc", label: "Carrier fees (calc)" },
              { key: "premium_tax_calc", label: "Premium tax (calc)" },
              { key: "fees_feed", label: "Total fees (feed)" }, { key: "variance", label: "Variance" },
            ]}
          >
            {chartFrame(
              <BarChart data={d.fee_breakdown || []}>
                {axes}{moneyAxis}
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="e1_fees_calc" name="E1 fees (calc)" stackId="f" fill={SERIES_COLORS.tech_fees} />
                <Bar dataKey="carrier_fees_calc" name="Carrier fees (calc)" stackId="f" fill={SERIES_COLORS.carrier_fees} />
                <Bar dataKey="premium_tax_calc" name="Premium tax (calc)" stackId="f" fill={SERIES_COLORS.management_fees} />
                <Bar dataKey="fees_feed" name="Total fees (feed)" fill={ESTIMATE_COLORS.fees} />
              </BarChart>
            )}
          </ChartCard>}

          {!isPP && <ChartCard
            testId="chart-claims-reserves" title="Claims & Reserves"
            note={chartNote(`Paid claims and held reserves per ${per}.`)}
            baseName="claims-and-reserves" hasData={hasFin} rows={d.claims_reserves || []}
            columns={[
              { key: "period", label: "Period" }, { key: "claims", label: "Claims" },
              { key: "reserves", label: "Reserves" },
              { key: "est_claims", label: "Claims (est)" }, { key: "est_reserves", label: "Reserves (est)" },
            ]}
          >
            {chartFrame(
              <BarChart data={d.claims_reserves || []} onClick={openDrill("financials", "Claims & reserves")} style={{ cursor: "pointer" }}>
                {axes}{moneyAxis}
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: "rgba(14,58,92,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="claims" name="Claims" stackId="c" fill={SERIES_COLORS.claims} />
                <Bar dataKey="reserves" name="Reserves" stackId="c" fill={SERIES_COLORS.reserves} />
                {showEstimates && <Bar dataKey="est_claims" name="Claims (est)" stackId="ce" fill={ESTIMATE_COLORS.claims} />}
                {showEstimates && <Bar dataKey="est_reserves" name="Reserves (est)" stackId="ce" fill={ESTIMATE_COLORS.reserves} />}
              </BarChart>
            )}
          </ChartCard>}

          {!isPP && invoiceService && <div className="lg:col-span-2">{vendorCard}</div>}
        </div>
      )}

      <DrillDialog
        drill={drill}
        filters={{ company, product, plan_type: planType, insurer, carrier,
                  fund: vendorFund === "ALL" ? "" : vendorFund }}
        onClose={() => setDrill(null)}
      />
    </div>
  );
}

function ControlSelect({ label, value, onChange, options, labels = {}, testId }) {
  if (!options.length) return null;
  return (
    <div>
      <div className="eyebrow text-[9px] mb-2">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="!rounded-none !bg-white min-w-[132px] h-9 text-xs" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="!rounded-none max-h-72">
          {options.map((option) => (
            <SelectItem key={option} value={option} className="!rounded-none text-xs">
              {labels[option] || option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
